import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import { getCurrentUserIsGuest, getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setShowInventory from "@insite/client-framework/Store/Context/Handlers/SetShowInventory";
import setShowPricing from "@insite/client-framework/Store/Context/Handlers/SetShowPricing";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import setIsReorderConfirmationModalOpen from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetIsReorderConfirmationModalOpen";
import setNextAction from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetNextAction";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
// import User from "@insite/mobius/Icons/User"; // Store Custom Code
// import Menu, { MappedLink } from "@insite/mobius/Menu"; // Store Custom Code
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
// import InjectableCss from "@insite/mobius/utilities/InjectableCss"; // Store Custom Code
import React, { useEffect } from "react";
// import { connect, ResolveThunks } from "react-redux"; // Store Custom Code
import { css, ThemeProps, withTheme } from "styled-components";

/* Store Custom Code - Start */
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import User from "@store/Library/Components/Icons/User";
import Menu, { MappedLink, MenuItemText, MenuPresentationProps } from "@store/MobiusOverrides/Menu/Menu";
import { getPolicy } from "@store/Framework/Store/Store/Data/getPolicy";
import { TYPOGRAPHY } from "@store/theme/common/design";
import breakpointValuesMediaQueries from "@store/Framework/Common/Utilities/breakpointValuesMediaQueries";
import { connect, ResolveThunks } from "react-redux";
import getColor from "@insite/mobius/utilities/getColor";
/* Store Custom Code - End */

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
    includeAccountMenu = "includeAccountMenu",
    showPricingToggle = "showPricingToggle",
    showInventoryToggle = "showInventoryToggle",
}

/* Store Custom Code - Start */
const mapStateToProps = (state: ApplicationState) => {
    const currentCart = getCurrentCartState(state).value;

    return {
        isSignInPage: getCurrentPage(state).type === "SignInPage",
        userName: state.context.session.userName,
        myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        isPunchOutSession: getIsPunchOutSession(state),
        currentLocation: getLocation(state),
        displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
        displayPricingAndInventory: state.context.session?.displayPricingAndInventory || false,
        showInventory: state.context.showInventory,
        showPricing: state.context.showPricing,
        isReorderConfirmationModalOpen: state.pages.vmiBins.isReorderConfirmationModalOpen,
        hasChangeLastQty:
            state.pages.vmiBins.selectVmiBinsChangedQty &&
            Object.keys(state.pages.vmiBins.selectVmiBinsChangedQty).length > 0,
        orderConfirmationPageUrl: getPageLinkByPageType(state, "OrderConfirmationPage")?.url,
        onePageCheckoutConfirmationPageUrl: getPageLinkByPageType(state, "OnePageCheckoutConfirmationPage")?.url,
        // Store Custom Code
        userLabel: state.context.session.userLabel,
        quickOrderLink: getPageLinkByPageType(state, "QuickOrderPage"),
        accountSettingsLink: getPageLinkByPageType(state, "AccountSettingsPage"),
        currentBillTo: getBillToState(state, currentCart?.billToId),
        currentShipTo: getShipToState(state, currentCart?.shipToId),
        changeCustomerLink: getPageLinkByPageType(state, "ChangeCustomerPage"),
        canSeeChangeCustomer: getPolicy(state, "Account.CanChangeCustomer"),
        session: state.context.session,
    };
};
/* Store Custom Code - End */

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    setIsReorderConfirmationModalOpen,
    setNextAction,
};

export interface HeaderSignInStyles {
    signOutWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    signOutClickable?: ClickablePresentationProps;
    // Store Custom Code
    labelWrapper?: InjectableCss;
    introTypography?: TypographyPresentationProps;
    titleTypographyWrapper?: InjectableCss;
    dropdownIcon?: IconPresentationProps;
    accountDropdownMenu?: MenuPresentationProps;
    cssOverrides?: {
        wrapper?: StyledProp<MenuPresentationProps>;
        menu?: StyledProp<MenuPresentationProps>;
        menuItem?: StyledProp<MenuPresentationProps>;
    };
}

export const headerSignInStyles: HeaderSignInStyles = {
    /* Store Custom Code - Start */
    signOutWrapper: {
        css: css`
            display: flex;

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: [null, null, null, "20px", null], properties: ["padding-right"] }],
                    mediaOptions.min,
                )}
        `,
    },
    titleClickable: {
        css: css`
            text-align: left;

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: ["0", null, null, "8px 10px 8px 0", null], properties: ["padding"] }],
                    mediaOptions.min,
                )}
        `,
    },
    titleIcon: {
        size: 20,
        css: css`
            justify-content: center;
            border-radius: 50%;

            width: 45px;
            height: 45px;
            background-color: ${getColor("secondary.main")};
            border: 2px solid ${getColor("secondary.main")};
            overflow: hidden;

            svg {
                width: 35px;
                height: 35px;
                margin-top: 10px;
                color: ${getColor("secondary.contrast")};
            }

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [
                        {
                            values: ["10px", null, null, "15px", null],
                            properties: ["margin-right"],
                        },
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    titleTypography: {
        weight: TYPOGRAPHY.weight.medium,
        ellipsis: true,
        lineHeight: TYPOGRAPHY.lineHeight.small,
        css: css`
            max-width: 200px;
            margin-right: 5px;
            color: ${getColor("header.contrast")};

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: ["14px", null, null, "16px", null], properties: ["font-size"] }],
                    mediaOptions.min,
                )}
        `,
    },
    signOutClickable: {
        css: css`
            font-size: ${TYPOGRAPHY.size.tiny};
            transform: normal;
            letter-spacing: 0.8px;
            color: ${getColor("text.dark")};
            padding: 0 5px;

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: [null, null, null, "4em", null], properties: ["min-width"] }],
                    mediaOptions.min,
                )}
        `,
    },
    /* Store Custom Code - End */
    // Store Custom Code
    labelWrapper: {
        css: css`
            flex-direction: column;

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: ["none", null, null, "flex", null], properties: ["display"] }],
                    mediaOptions.min,
                )}
        `,
    },
    introTypography: {
        weight: TYPOGRAPHY.weight.medium,
        lineHeight: TYPOGRAPHY.lineHeight.small,
        css: css`
            color: ${getColor("text.lighter")};
            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: ["14px", null, null, "16px", null], properties: ["font-size"] }],
                    mediaOptions.min,
                )}
        `,
    },
    titleTypographyWrapper: {
        css: css`
            align-items: center;

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: ["none", null, null, "flex", null], properties: ["display"] }],
                    mediaOptions.min,
                )}
        `,
    },
    dropdownIcon: {
        src: ChevronDown,
        size: 16,
        css: css`
            svg {
                color: ${getColor("text.main")};
            }
        `,
    },
    accountDropdownMenu: {
        cssOverrides: {
            wrapper: css`
                flex: 0 1 auto;
                min-width: 0;
                text-align: left;

                ${({ theme }) =>
                    breakpointValuesMediaQueries(
                        theme,
                        [{ values: ["static", "relative", null, null, null], properties: ["position"] }],
                        mediaOptions.min,
                    )}
            `,
            menu: css`
                min-width: 300px;
                right: 0;
                padding: 8px 0;

                ${({ theme }) =>
                    breakpointValuesMediaQueries(
                        theme,
                        [
                            { values: ["inherit", "0", null, null, null], properties: ["right"] },
                            { values: ["50%", "inherit", null, null, null], properties: ["left"] },
                            { values: ["translate(-50%)", "inherit", null, null, null], properties: ["transform"] },
                            { values: ["90%", "100%", null, null, null], properties: ["width"] },
                        ],
                        mediaOptions.min,
                    )}
            `,
            menuItem: css`
                a {
                    min-height: inherit;
                    justify-content: space-between;
                    text-decoration: underline;
                    color: ${getColor("secondary.main")};

                    &:hover {
                        background: transparent;
                        text-decoration: none;
                    }
                }

                ${MenuItemText} {
                    padding: 7px 20px;
                    color: ${getColor("secondary.main")};
                    font-size: ${TYPOGRAPHY.size.smaller};
                }
            `,
        },
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: string;
        [fields.icon]: string;
        [fields.includeAccountMenu]: boolean;
        [fields.showPricingToggle]: boolean;
        [fields.showInventoryToggle]: boolean;
    };
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    ThemeProps<BaseTheme>;

let icon: React.ComponentType<IconPresentationProps>;

export const HeaderSignIn = ({
    userName,
    isSignInPage,
    currentUserIsGuest,
    signOut,
    cancelPunchOut,
    fields,
    history,
    myAccountPageLink,
    signInUrl,
    isPunchOutSession,
    currentLocation,
    displayChangeCustomerLink,
    displayPricingAndInventory,
    showInventory,
    showPricing,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    theme,
    isReorderConfirmationModalOpen,
    hasChangeLastQty,
    setIsReorderConfirmationModalOpen,
    setNextAction,
    orderConfirmationPageUrl,
    onePageCheckoutConfirmationPageUrl,
    // Store Custom Code
    userLabel,
    quickOrderLink,
    accountSettingsLink,
    currentBillTo,
    currentShipTo,
    changeCustomerLink,
    canSeeChangeCustomer,
    session,
}: Props) => {
    const styles = useMergeStyles("headerSignIn", headerSignInStyles);

    useEffect(() => {
        window.onpageshow = function (event) {
            if (event.persisted) {
                window.location.reload();
            }
        };
    }, [userName]);

    if (!myAccountPageLink) {
        return null;
    }

    const { visibilityState, includeAccountMenu, showInventoryToggle, showPricingToggle } = fields;
    const showIcon = visibilityState === "both" || visibilityState === "icon";
    const showLabel = visibilityState === "both" || visibilityState === "label";

    if (fields.icon === "User") {
        icon = User;
    }

    /* Store Custom Code - Start */
    const customerInformation = {
        currentBillTo,
        currentShipTo,
    };
    /* Store Custom Code - End */

    const currentMode = getCookie(NavigationModeCookieName);
    /* Store Custom Code - Start */
    let menuItems = (myAccountPageLink?.children?.filter(
        pageLink =>
            !(pageLink.type === "ChangeCustomerPage" && !displayChangeCustomerLink) &&
            pageLink.type !== "OrderApprovalListPage" &&
            pageLink.type !== "BudgetManagementPage" &&
            pageLink.type !== "RequisitionsPage" &&
            pageLink.type !== "AddressesPage" &&
            pageLink.type !== "InvoiceHistoryPage",
    ) ?? []) as MappedLink[];

    if (myAccountPageLink && !myAccountPageLink.excludeFromNavigation) {
        menuItems.splice(0, 0, { title: myAccountPageLink.title, url: myAccountPageLink.url }); // 1st position
    }

    if (!isPunchOutSession && quickOrderLink) {
        menuItems.splice(3, 0, { title: quickOrderLink.title, url: quickOrderLink.url }); // 2nd position
    }

    if (accountSettingsLink && !accountSettingsLink.excludeFromNavigation) {
        const index = menuItems.findIndex(item => item.type === "AccountSettingsPage");
        if (index !== -1) {
            menuItems.splice(index, 1); // Removed from its position
        }

        menuItems.splice(menuItems.length - 1, 0, {
            title: accountSettingsLink.title,
            url: accountSettingsLink.url,
        }); // And moved to n-1 position
    }
    /* Store Custom Code - End */

    const getMenuItem = (title: "Inventory" | "Pricing"): MappedLink => {
        return {
            title: title === "Inventory" ? translate("Inventory") : translate("Pricing"),
            iconProps: {
                src: (title === "Inventory" && showInventory) || (title === "Pricing" && showPricing) ? EyeOff : Eye,
                size: 22,
                css: css`
                    margin-left: 20px;
                    margin-right: -12px;
                `,
            },
            clickableProps: {
                css: css`
                    display: flex;
                    text-transform: uppercase;
                `,
                onClick: () => {
                    title === "Inventory"
                        ? setShowInventory({ showInventory: !showInventory })
                        : setShowPricing({ showPricing: !showPricing });
                },
            },
        };
    };

    if (!currentMode || currentMode === "Storefront") {
        menuItems = menuItems?.filter(pageLink => pageLink.type !== "VmiUsersPage");

        showInventoryToggle && displayPricingAndInventory && menuItems.push(getMenuItem("Inventory"));
        showPricingToggle && displayPricingAndInventory && menuItems.push(getMenuItem("Pricing"));
    }

    const onSignInHandler = (e: React.MouseEvent) => {
        if ((!userName || currentUserIsGuest) && !isSignInPage) {
            e.preventDefault();

            if (theme.signIn?.useOverlay) {
                setSignInOverlayIsOpen({ isOpen: true });
                return;
            }

            if (!signInUrl) {
                logger.warn("No url was found for SignInPage, defaulting to /SignIn");
                history.push("/MyAccount/SignIn");
                return;
            }

            const returnUrl = currentLocation.pathname + currentLocation.search;
            if (
                returnUrl === "/" ||
                (currentUserIsGuest && orderConfirmationPageUrl && returnUrl.indexOf(orderConfirmationPageUrl) > -1) ||
                (currentUserIsGuest &&
                    onePageCheckoutConfirmationPageUrl &&
                    returnUrl.indexOf(onePageCheckoutConfirmationPageUrl) > -1)
            ) {
                history.push(`${signInUrl}`);
                return;
            }

            history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`);
        }
    };

    const onSignOutHandler = () => {
        if (!isReorderConfirmationModalOpen && hasChangeLastQty) {
            setIsReorderConfirmationModalOpen({ isReorderConfirmationModalOpen: true });
            setNextAction({ nextAction: signOutFunc });
        } else {
            signOutFunc();
        }
    };

    const signOutFunc = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    let signInStatusText = translate("Sign In");
    if (userName && !currentUserIsGuest) {
        /* Store Custom Code - Start */
        if (userLabel) {
            signInStatusText = userLabel;
        } else {
            signInStatusText = userName;
        }
        /* Store Custom Code - End */
    }

    return (
        <StyledWrapper {...styles.signOutWrapper}>
            {includeAccountMenu && userName && !currentUserIsGuest && (
                <Menu
                    descriptionId="accountMenu"
                    menuItems={menuItems}
                    displayChangeCustomerLink={canSeeChangeCustomer} // Store Custom Code
                    maxDepth={1}
                    // Store Custom Code
                    {...styles.accountDropdownMenu}
                    isAccountMenu={true}
                    customerInformation={customerInformation}
                    customerSelectionUrl={changeCustomerLink?.url}
                    onSignOutHandler={onSignOutHandler}
                    canSeeChangeCustomer={canSeeChangeCustomer}
                    isPunchOutSession={isPunchOutSession}
                    menuTrigger={
                        <Clickable {...styles.titleClickable} data-test-selector="header_signIn">
                            {showIcon && (
                                <Icon
                                    {...styles.titleIcon}
                                    src={icon}
                                    title={signInStatusText} /* Store Custom Code */
                                />
                            )}
                            {showLabel && (
                                /* Store Custom Code - Start */
                                <StyledWrapper {...styles.labelWrapper}>
                                    <Typography {...styles.introTypography}>{translate("Welcome")}</Typography>
                                    <StyledWrapper {...styles.titleTypographyWrapper}>
                                        <Typography {...styles.titleTypography} data-test-selector="header_userName">
                                            {signInStatusText}
                                        </Typography>
                                        <Icon {...styles.dropdownIcon} />
                                    </StyledWrapper>
                                </StyledWrapper>
                                /* Store Custom Code - End */
                            )}
                        </Clickable>
                    }
                    // Store Custom Code - Removed - cssOverrides
                    data-test-selector="header_accountMenu"
                />
            )}
            {(!includeAccountMenu || !userName || (userName && currentUserIsGuest)) && (
                <Clickable
                    {...styles.titleClickable}
                    onClick={onSignInHandler}
                    href={!signInUrl ? "/MyAccount/SignIn" : signInUrl}
                    data-test-selector="header_signIn"
                >
                    {showIcon && <Icon {...styles.titleIcon} src={icon} />}
                    {showLabel && <Typography {...styles.titleTypography}>{signInStatusText}</Typography>}
                </Clickable>
            )}
            {/* Store Custom Code - Removed signOut button */}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(HeaderSignIn))),
    definition: {
        displayName: "Header Sign In",
        icon: "arrow-right-to-bracket",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "SignIn Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "User",
                        value: "User",
                    },
                ],
                defaultValue: "User",
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.includeAccountMenu,
                displayName: "Include My Account Menu",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            // Store Custom Code - Removed - fields.showPricingToggle
            // Store Custom Code - Removed - fields.showInventoryToggle
        ],
        group: "Common",
    },
};

export default widgetModule;
